import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  Shield, 
  Users, 
  TrendingUp, 
  CheckCircle2, 
  AlertCircle,
  Handshake,
  Eye,
  Lock,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

const whatYouGet = [
  {
    icon: CheckCircle2,
    title: "Pre-Validated Products",
    description: "Every product we introduce has real customers, proven demand, and validated pricing. No pitch decks built on hope.",
  },
  {
    icon: Users,
    title: "Serious Founders",
    description: "Founders who have invested their own time, energy, and often capital. They've done the hard work of validation.",
  },
  {
    icon: TrendingUp,
    title: "Real Traction",
    description: "Products with actual paying customers. Not projections, not promises. Real revenue, real market validation.",
  },
  {
    icon: Eye,
    title: "Full Transparency",
    description: "We share what we know, nothing hidden. You see the same data we do. Make your own informed decisions.",
  },
];

const howItWorks = [
  {
    step: "01",
    title: "Register Your Interest",
    description: "Tell us about yourself and what kind of opportunities you're looking for. No commitment, just an introduction.",
  },
  {
    step: "02",
    title: "We Share Opportunities",
    description: "When we have founders ready for investment conversations (and only when they request it), we'll make an introduction.",
  },
  {
    step: "03",
    title: "Direct Conversations",
    description: "You connect directly with the founder. We step back. No interference, no influence, no middleman games.",
  },
  {
    step: "04",
    title: "You Decide",
    description: "Every decision is yours and the founder's. We don't push, don't promise outcomes, don't take sides.",
  },
];

const commitments = [
  {
    icon: Shield,
    title: "We Don't Promise Founders Investment",
    description: "We never tell founders we'll get them funded. We can only offer to make an introduction if they want one.",
  },
  {
    icon: Handshake,
    title: "Introductions Only on Request",
    description: "We only connect founders with investors when the founder explicitly asks. Never without their consent.",
  },
  {
    icon: Lock,
    title: "We Don't Take a Cut",
    description: "We have no financial interest in any deal. Zero commission, zero finder's fees. This isn't a revenue stream for us.",
  },
  {
    icon: Eye,
    title: "Complete Transparency",
    description: "We share everything we know. No hidden information, no spin. Both parties get the same honest picture.",
  },
];

export default function InvestorsPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    firm: "",
    website: "",
    investment_range: "",
    sectors: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${API_URL}/api/investors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({
          name: "",
          email: "",
          firm: "",
          website: "",
          investment_range: "",
          sectors: "",
          message: "",
        });
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="bg-hero section-padding">
        <div className="container-narrow">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeUp}
            className="text-center"
          >
            <h1 className="heading-display text-foreground mb-6">
              For Investors
            </h1>
            <p className="body-large text-muted-foreground max-w-2xl mx-auto mb-4">
              Access pre-validated products with real customers and serious founders. 
              No middleman games. No hidden agendas. Just honest introductions.
            </p>
            <p className="text-sm text-muted-foreground">
              We don't make money from this. We just believe good founders deserve to meet good investors.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Approach - Blunt Honesty */}
      <section className="bg-background section-padding">
        <div className="container-base">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <div className="bg-muted/30 border border-border rounded-2xl p-8 md:p-10">
              <div className="flex items-start gap-4 mb-6">
                <AlertCircle className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-serif text-2xl font-medium text-foreground mb-2">
                    Let's Be Blunt
                  </h2>
                  <p className="text-muted-foreground">
                    Before you register, here's exactly how this works. No fine print.
                  </p>
                </div>
              </div>
              
              <div className="space-y-4 text-muted-foreground">
                <p>
                  <strong className="text-foreground">We are not a fundraising platform.</strong> We don't promise founders that we'll get them investors. We don't promise investors that they'll find the next unicorn.
                </p>
                <p>
                  <strong className="text-foreground">We build products and get customers.</strong> That's our core work. Some of these products become investment-ready. Some founders want introductions. When that happens, we can connect you.
                </p>
                <p>
                  <strong className="text-foreground">We step back after the introduction.</strong> We don't sit in on meetings. We don't negotiate. We don't influence. The conversation is between you and the founder, period.
                </p>
                <p>
                  <strong className="text-foreground">We have no financial interest.</strong> We don't take finder's fees, carry, or any cut of any deal. This is not a revenue stream for Let's Build Startup. We do this because we believe in the founders we work with.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What You Get */}
      <section className="bg-section section-padding">
        <div className="container-base">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="heading-section text-foreground mb-4">What You Get Access To</h2>
            <p className="body-base text-muted-foreground max-w-2xl mx-auto">
              Not pitch decks. Not projections. Real products with real validation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {whatYouGet.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <Card className="card-elevated h-full">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <item.icon className="h-5 w-5 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-serif text-lg font-medium text-foreground mb-2">
                          {item.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-background section-padding">
        <div className="container-base">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="heading-section text-foreground mb-4">How It Works</h2>
            <p className="body-base text-muted-foreground max-w-2xl mx-auto">
              Simple, transparent, no hidden steps.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((step, i) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <span className="font-serif text-lg font-medium text-accent">{step.step}</span>
                </div>
                <h3 className="font-serif text-lg font-medium text-foreground mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Commitments */}
      <section className="bg-section section-padding">
        <div className="container-base">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="heading-section text-foreground mb-4">Our Commitments</h2>
            <p className="body-base text-muted-foreground max-w-2xl mx-auto">
              These are not marketing promises. This is how we actually operate.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {commitments.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="flex items-start gap-4 p-6 bg-background rounded-xl border border-border/50">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground mb-1">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="bg-background section-padding" id="register">
        <div className="container-narrow">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-10">
              <h2 className="heading-section text-foreground mb-4">Register Your Interest</h2>
              <p className="body-base text-muted-foreground max-w-xl mx-auto">
                Tell us about yourself. No commitment, no obligations. We'll reach out when we have relevant opportunities.
              </p>
            </div>

            <Card className="card-elevated max-w-2xl mx-auto">
              <CardContent className="p-8">
                {submitStatus === "success" ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="font-serif text-xl font-medium text-foreground mb-2">
                      Thank You
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      We've received your information. We'll be in touch when we have relevant opportunities to share.
                    </p>
                    <Button 
                      variant="outline" 
                      onClick={() => setSubmitStatus(null)}
                      data-testid="register-another-btn"
                    >
                      Register Another Contact
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Your name"
                          data-testid="investor-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="you@example.com"
                          data-testid="investor-email-input"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="firm">Firm / Organization</Label>
                        <Input
                          id="firm"
                          name="firm"
                          value={formData.firm}
                          onChange={handleChange}
                          placeholder="Your firm name (if applicable)"
                          data-testid="investor-firm-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="website">Website / LinkedIn</Label>
                        <Input
                          id="website"
                          name="website"
                          value={formData.website}
                          onChange={handleChange}
                          placeholder="https://"
                          data-testid="investor-website-input"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="investment_range">Typical Investment Range</Label>
                        <Input
                          id="investment_range"
                          name="investment_range"
                          value={formData.investment_range}
                          onChange={handleChange}
                          placeholder="e.g., $50K - $500K"
                          data-testid="investor-range-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="sectors">Sectors of Interest</Label>
                        <Input
                          id="sectors"
                          name="sectors"
                          value={formData.sectors}
                          onChange={handleChange}
                          placeholder="e.g., SaaS, FinTech, HealthTech"
                          data-testid="investor-sectors-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Anything else we should know?</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Investment thesis, preferred stage, geographic focus, etc."
                        rows={4}
                        data-testid="investor-message-input"
                      />
                    </div>

                    {submitStatus === "error" && (
                      <div className="bg-red-50 text-red-600 p-4 rounded-lg text-sm">
                        Something went wrong. Please try again or email us directly.
                      </div>
                    )}

                    <Button
                      type="submit"
                      variant="accent"
                      size="lg"
                      className="w-full rounded-full"
                      disabled={isSubmitting}
                      data-testid="investor-submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Register Interest
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-muted-foreground text-center">
                      By registering, you agree to receive occasional updates about investment opportunities. 
                      We don't spam. We don't sell your data. Unsubscribe anytime.
                    </p>
                  </form>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Final Note */}
      <section className="bg-section section-padding-sm">
        <div className="container-narrow text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Questions about how this works? Want to understand our process better? 
              We're happy to talk. No pitch, just a conversation.
            </p>
            <Button variant="outline" size="lg" asChild className="rounded-full mt-6">
              <Link to="/contact" data-testid="investor-contact-link">
                Get in Touch
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
